// Dictation runs Whistle, Cactus Compute's speech model, in the browser through cactus-needle: audio never leaves the
// device. Its code is a chunk of this app, loaded on the first press; the 16.9 MB model then downloads once from
// Hugging Face, pinned to a revision and checksum by the package, and the browser caches it.

/** Whistle's sample rate: mono, 16 kHz. */
export const SAMPLE_RATE = 16_000
const MAX_SECONDS = 120

type Speech = {
  stream(chunks: AsyncIterable<Float32Array>): AsyncIterable<{ text: string; pending: string }>
}

/** What a dictation shows while it runs. `loading` is the model's download, in percent, until it is ready. */
export type DictationState = { text: string; level: number; loading: number | null }

export type Dictation = {
  /** Stops listening and resolves with everything that was said, once Whistle has caught up. */
  finish(): Promise<string>
  /** Stops listening and drops what was said. */
  cancel(): void
}

/** Whether this browser can record and run Whistle. */
export const dictationSupported = () =>
  window.isSecureContext &&
  // Missing outside secure pages and in some webviews, whatever its type says.
  'mediaDevices' in navigator &&
  typeof navigator.mediaDevices.getUserMedia === 'function' &&
  typeof AudioContext === 'function' &&
  typeof Worker === 'function' &&
  typeof WebAssembly === 'object'

let whistle: Promise<Speech> | undefined
let reportProgress: ((percentage: number) => void) | undefined
// Whistle takes one stream at a time, so a dictation waits for the one before it to be written out.
let previous: Promise<unknown> = Promise.resolve()

/** Loads Whistle once per page and keeps it for every dictation after. */
function loadWhistle() {
  whistle ??= (async () => {
    const [{ createWhistle }, { default: workerUrl }, { default: wasmUrl }] = await Promise.all([
      import('cactus-needle/browser'),
      import('cactus-needle/browser/worker.js?url'),
      import('cactus-needle/browser/needle.wasm?url'),
    ])
    return createWhistle({
      workerUrl,
      wasmUrl,
      onDownloadProgress: ({ percentage }) =>
        percentage !== undefined && reportProgress?.(Math.round(percentage)),
    })
  })().catch((error: unknown) => {
    whistle = undefined
    throw error
  })
  return whistle
}

/** Starts listening to the microphone. Rejects when the microphone isn't allowed. */
export async function startDictation({
  onChange,
  onStop,
}: {
  onChange: (state: DictationState) => void
  /** Called when the dictation stops listening on its own: at its length limit, or when Whistle fails to load. */
  onStop: () => void
}): Promise<Dictation> {
  // Made before asking for the microphone, while the press still counts as one: made after a slow permission
  // prompt, the browser can start it suspended, and then it hears nothing.
  const context = new AudioContext()
  let media: MediaStream
  try {
    media = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
    })
  } catch (error) {
    void context.close()
    throw error
  }
  let source: MediaStreamAudioSourceNode
  let processor: ScriptProcessorNode
  try {
    if (context.state === 'suspended') void context.resume()
    source = context.createMediaStreamSource(media)
    // Deprecated, but unlike an AudioWorklet it needs no module of its own.
    processor = context.createScriptProcessor(4096, 1, 1)
    source.connect(processor)
    // A ScriptProcessor only runs while connected to the output. It writes nothing, so nothing is heard.
    processor.connect(context.destination)
  } catch (error) {
    // The microphone is already on, and nothing else could turn it off.
    for (const track of media.getTracks()) track.stop()
    void context.close()
    throw error
  }
  const resample = resampler(context.sampleRate)
  const chunks = new ChunkQueue()
  const state: DictationState = { text: '', level: 0, loading: null }
  let committed = ''
  let pending = ''
  let buffered: Float32Array[] = []
  let bufferedLength = 0
  let received = 0
  let listening = true
  let canceled = false

  const show = (changes: Partial<DictationState>) => {
    Object.assign(state, changes)
    if (!canceled) onChange({ ...state })
  }
  const flush = () => {
    if (!bufferedLength) return
    chunks.push(concat(buffered, bufferedLength))
    buffered = []
    bufferedLength = 0
  }
  const stop = () => {
    if (!listening) return
    listening = false
    flush()
    chunks.close()
    processor.disconnect()
    source.disconnect()
    for (const track of media.getTracks()) track.stop()
    void context.close()
  }

  processor.onaudioprocess = ({ inputBuffer }: AudioProcessingEvent) => {
    if (!listening) return
    const input = inputBuffer.getChannelData(0)
    let sum = 0
    for (const sample of input) sum += sample * sample
    show({ level: Math.min(1, Math.sqrt(sum / input.length) * 8) })
    const samples = resample(input)
    buffered.push(samples)
    bufferedLength += samples.length
    received += samples.length
    // About a second at a time, as Whistle streams best.
    if (bufferedLength >= SAMPLE_RATE) flush()
    if (received >= MAX_SECONDS * SAMPLE_RATE) {
      stop()
      onStop()
    }
  }

  // Audio buffers while the model loads, and is written out as soon as it is ready.
  reportProgress = (loading) => show({ loading })
  const transcribed = previous
    .catch(() => {})
    .then(async () => {
      show({ loading: 0 })
      const speech = await loadWhistle()
      show({ loading: null })
      for await (const part of speech.stream(chunks)) {
        committed = join(committed, part.text)
        pending = part.pending
        show({ text: join(committed, pending) })
      }
      return join(committed, pending)
    })
  previous = transcribed
  // A model that can't load ends the dictation now, rather than recording until someone presses a button.
  transcribed.catch(() => {
    if (!listening) return
    stop()
    onStop()
  })

  return {
    finish: () => {
      stop()
      return transcribed
    },
    cancel: () => {
      canceled = true
      stop()
    },
  }
}

const join = (text: string, more: string) => [text.trim(), more.trim()].filter(Boolean).join(' ')

function concat(parts: Float32Array[], length: number) {
  const joined = new Float32Array(length)
  let offset = 0
  for (const part of parts) {
    joined.set(part, offset)
    offset += part.length
  }
  return joined
}

/** Turns audio at `from` Hz into 16 kHz, averaging the samples each output sample stands for. */
export function resampler(from: number) {
  const step = from / SAMPLE_RATE
  // Where the next output sample starts, in samples of the next input buffer.
  let position = 0
  return (input: Float32Array) => {
    const output: number[] = []
    while (position + step <= input.length) {
      const start = Math.max(0, Math.floor(position))
      const end = Math.max(start + 1, Math.floor(position + step))
      let sum = 0
      for (let at = start; at < end; at++) sum += input[at]
      output.push(sum / (end - start))
      position += step
    }
    position -= input.length
    return Float32Array.from(output)
  }
}

/** Audio chunks as they are recorded, for Whistle to read as they come. */
export class ChunkQueue implements AsyncIterable<Float32Array> {
  private chunks: Float32Array[] = []
  private waiting: (() => void) | undefined
  private closed = false

  push(chunk: Float32Array) {
    this.chunks.push(chunk)
    this.waiting?.()
  }

  close() {
    this.closed = true
    this.waiting?.()
  }

  async *[Symbol.asyncIterator]() {
    for (;;) {
      const chunk = this.chunks.shift()
      if (chunk) yield chunk
      else if (this.closed) return
      else await new Promise<void>((resolve) => (this.waiting = resolve))
    }
  }
}
